/**
 * Tenant Admin – Subscription Management (Phase 5.2)
 * View current plan, quotas, approve/reject pending, buy top-ups.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import api from '@/services/api';
import { ModelUsageReport } from '@/pages/ai/ModelUsageReport';
import { useAuth } from '@/state/auth/AuthProvider';
import { StripePaymentForm } from '@/components/billing/StripePaymentForm';
import { StripeSetupForm } from '@/components/billing/StripeSetupForm';
import { useLanguage } from '@/state/language/LanguageProvider';

interface SubscriptionRow {
  id: string;
  planName: string;
  tierName: string;
  status: string;
  periodStart: string;
  periodEnd: string;
  cancelAtPeriodEnd: boolean;
  billingPeriod: string;
  priceUsd: number;
  currency: string;
  dailyTokenCap: number;
  defaultModelDisplayName?: string | null;
  /** True when subscription is linked to Stripe; only then can user update payment method. */
  hasLinkedPaymentProvider?: boolean;
  /** Owner user display name (for tenant config list). */
  ownerUserName?: string | null;
}

interface PendingRow {
  id: string;
  ownerUserId: string | null;
  planName: string;
  tierName: string;
  createdAt: string;
}

interface TopUpRow {
  id: string;
  subscriptionId: string;
  amountUsd: number;
  currency: string;
  purchasedAt: string;
}

interface PlanOption {
  id: string;
  name: string;
  planCode: string;
  billingPeriod: string;
  priceUsd: number;
  currency: string;
  aiTierId: string;
}

interface TenantSubscriptionPageProps {
  embedded?: boolean;
  /** When true (e.g. tenant config page), hide top-up section; top-up is per user, not tenant-level. */
  hideTopUpSection?: boolean;
  initialAttemptId?: string;
  initialPlanId?: string;
  onPlanAttemptConsumed?: () => void;
}

export const TenantSubscriptionPage: React.FC<TenantSubscriptionPageProps> = ({
  embedded = false,
  hideTopUpSection = false,
  initialAttemptId,
  initialPlanId,
  onPlanAttemptConsumed,
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const role = user?.role ?? '';
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [topups, setTopups] = useState<TopUpRow[]>([]);
  const [topUpBalanceUsd, setTopUpBalanceUsd] = useState<number>(0);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [subscribePlanId, setSubscribePlanId] = useState('');
  const [subscribeTierId, setSubscribeTierId] = useState('');
  const [consumedAttemptId, setConsumedAttemptId] = useState<string | null>(null);
  const [topUpSubscriptionId, setTopUpSubscriptionId] = useState('');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [tenantType, setTenantType] = useState<string>('ORGANIZATION');
  const isIndividualTenant = tenantType === 'INDIVIDUAL';
  const isTenantOrPlatformAdmin = role === 'Tenant Administrator' || role === 'Platform Administrator';
  const [readOnly, setReadOnly] = useState(false);
  // Hybrid: when embedded (profile) or admin/individual tenant, user can manage subscriptions.
  const canManage = !readOnly && (isIndividualTenant || isTenantOrPlatformAdmin || embedded);
  const [subscribePromoCode, setSubscribePromoCode] = useState('');
  const [subscribePricePreview, setSubscribePricePreview] = useState<{
    basePriceUsd: number;
    discountUsd: number;
    finalPriceUsd: number;
    promotionCode: string | null;
    promotionName: string | null;
  } | null>(null);
  const [topUpPromoCode, setTopUpPromoCode] = useState('');
  const [topUpPricePreview, setTopUpPricePreview] = useState<{
    basePriceUsd: number;
    discountUsd: number;
    finalPriceUsd: number;
    promotionCode: string | null;
    promotionName: string | null;
  } | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [paymentModal, setPaymentModal] = useState<'subscription' | 'topup' | 'update-card' | null>(null);
  const [updateCardSubscriptionId, setUpdateCardSubscriptionId] = useState<string | null>(null);
  const [updateCardSetupIntentId, setUpdateCardSetupIntentId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [contextRes, subsRes, pendingRes, topupsRes, plansRes, stripeConfigRes] = await Promise.all([
        api.get<{ tenantType: string }>('/ai/billing/tenant/context').catch(() => ({ data: { tenantType: 'ORGANIZATION' } })),
        api.get<{ subscriptions: SubscriptionRow[]; readOnly?: boolean }>(
          embedded && !hideTopUpSection
            ? '/ai/billing/tenant/subscriptions?scope=self'
            : '/ai/billing/tenant/subscriptions'
        ),
        api.get<{ pending: PendingRow[] }>('/ai/billing/tenant/pending'),
        hideTopUpSection
          ? Promise.resolve({ data: { topups: [] as TopUpRow[], topUpBalanceUsd: 0 } })
          : api.get<{ topups: TopUpRow[]; topUpBalanceUsd: number }>('/ai/billing/tenant/topups'),
        api.get<{ plans: PlanOption[] }>('/ai/billing/tenant/plans'),
        api.get<{ publishableKey: string | null }>('/ai/billing/tenant/stripe-config').catch(() => ({ data: { publishableKey: null } })),
      ]);
      setTenantType(contextRes.data?.tenantType ?? 'ORGANIZATION');
      setSubscriptions(subsRes.data?.subscriptions ?? []);
      setReadOnly(subsRes.data?.readOnly === true);
      setPending(pendingRes.data?.pending ?? []);
      setTopups(topupsRes.data?.topups ?? []);
      setTopUpBalanceUsd(topupsRes.data?.topUpBalanceUsd ?? 0);
      setPlans(plansRes.data?.plans ?? []);
      const pk = stripeConfigRes.data?.publishableKey ?? (import.meta as unknown as { env?: { VITE_STRIPE_PUBLISHABLE_KEY?: string } }).env?.VITE_STRIPE_PUBLISHABLE_KEY ?? null;
      setStripePublishableKey(pk || null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string; message?: string }; status?: number } };
      const status = err.response?.status;
      const message = err.response?.data?.message || err.response?.data?.error || (e instanceof Error ? e.message : 'Failed to load');
      if (status === 403 && embedded) {
        setError(t('subscriptions.profileTabBillingRestricted') || 'Subscription details are managed by your organization. Contact your administrator to view or change your plan.');
        // Do not toast "Access denied" when embedded in profile — the inline message is enough
      } else {
        setError(message);
        if (status === 403) toast.error(t('navigation.accessDenied') || 'Access denied');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  // When arriving with plan-attempt params (e.g. after login/signup), open subscribe modal and consume attempt
  useEffect(() => {
    if (!initialAttemptId || consumedAttemptId === initialAttemptId || loading) return;
    const openAndConsume = async () => {
      if (initialPlanId && plans.length > 0) {
        const plan = plans.find((p) => p.id === initialPlanId);
        if (plan) {
          setSubscribePlanId(initialPlanId);
          setSubscribeTierId(plan.aiTierId);
        }
      } else if (initialPlanId) {
        setSubscribePlanId(initialPlanId);
      }
      setShowSubscribeModal(true);
      try {
        await api.post(`/auth/plan-attempt/${initialAttemptId}/consume`);
        setConsumedAttemptId(initialAttemptId);
        onPlanAttemptConsumed?.();
      } catch {
        // Non-blocking; modal is already open
      }
    };
    void openAndConsume();
  }, [initialAttemptId, initialPlanId, loading, plans, consumedAttemptId, onPlanAttemptConsumed]);

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await api.post(`/ai/billing/subscriptions/${id}/approve`);
      toast.success('Subscription approved.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      await api.put(`/ai/billing/subscriptions/${id}`, { status: 'cancelled' });
      toast.success('Subscription request rejected.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (id: string, atPeriodEnd: boolean) => {
    setActionLoading(id);
    try {
      await api.put(`/ai/billing/subscriptions/${id}`, {
        cancelAtPeriodEnd: atPeriodEnd,
        ...(atPeriodEnd ? {} : { status: 'cancelled' }),
      });
      toast.success(atPeriodEnd ? 'Subscription will cancel at period end.' : 'Subscription cancelled.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateSubscription = async () => {
    if (!subscribePlanId || !subscribeTierId) {
      toast.error('Select a plan.');
      return;
    }
    if (!stripePublishableKey) {
      toast.error('Payment is not configured. Contact support.');
      return;
    }
    setActionLoading('subscribe');
    try {
      const res = await api.post<{ clientSecret?: string }>('/ai/billing/subscriptions', {
        billingPlanId: subscribePlanId,
        aiTierId: subscribeTierId,
        status: 'active',
      });
      const clientSecret = res.data?.clientSecret;
      if (clientSecret) {
        setPaymentClientSecret(clientSecret);
        setPaymentModal('subscription');
        setActionLoading(null);
        return;
      }
      toast.success('Subscription created.');
      setShowSubscribeModal(false);
      setSubscribePlanId('');
      setSubscribeTierId('');
      setSubscribePromoCode('');
      setSubscribePricePreview(null);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to create subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBuyTopUp = async () => {
    const subId = topUpSubscriptionId || subscriptions.find((s) => s.status === 'active')?.id;
    if (!subId) {
      toast.error('Select an active subscription.');
      return;
    }
    const amount = parseFloat(topUpAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid amount.');
      return;
    }
    if (!stripePublishableKey) {
      toast.error('Payment is not configured. Contact support.');
      return;
    }
    setActionLoading('topup');
    try {
      const res = await api.post<{ clientSecret?: string; message?: string }>('/ai/billing/topups', {
        subscriptionId: subId,
        amountUsd: amount,
        currency: 'usd',
      });
      if (res.data?.clientSecret) {
        setPaymentClientSecret(res.data.clientSecret);
        setPaymentModal('topup');
        setActionLoading(null);
        return;
      }
      toast.success('Top-up added.');
      setShowTopUpModal(false);
      setTopUpSubscriptionId('');
      setTopUpAmount('');
      setTopUpPromoCode('');
      setTopUpPricePreview(null);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to create top-up');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateCardClick = async (subscriptionId: string) => {
    if (!stripePublishableKey) {
      toast.error('Payment is not configured. Contact support.');
      return;
    }
    setActionLoading(subscriptionId);
    try {
      const res = await api.post<{ clientSecret: string; setupIntentId: string }>(
        `/ai/billing/subscriptions/${subscriptionId}/payment-method/setup-intent`
      );
      setPaymentClientSecret(res.data.clientSecret);
      setUpdateCardSubscriptionId(subscriptionId);
      setUpdateCardSetupIntentId(res.data.setupIntentId);
      setPaymentModal('update-card');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string; message?: string } } };
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to start update');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePaymentSuccess = (mode: 'subscription' | 'topup') => {
    setPaymentClientSecret(null);
    setPaymentModal(null);
    if (mode === 'subscription') {
      setShowSubscribeModal(false);
      setSubscribePlanId('');
      setSubscribeTierId('');
      setSubscribePromoCode('');
      setSubscribePricePreview(null);
      toast.success('Subscription created.');
      void load();
    } else {
      setShowTopUpModal(false);
      setTopUpSubscriptionId('');
      setTopUpAmount('');
      setTopUpPromoCode('');
      setTopUpPricePreview(null);
      toast.success('Top-up added.');
      void load();
      // Webhook creates the top-up; retry load so the list updates as soon as backend has it
      window.setTimeout(() => void load(), 1500);
      window.setTimeout(() => void load(), 3500);
    }
  };

  const handlePaymentCancel = () => {
    setPaymentClientSecret(null);
    setPaymentModal(null);
    setUpdateCardSubscriptionId(null);
    setUpdateCardSetupIntentId(null);
  };

  if (loading) {
    return embedded ? (
      <p className="text-sm text-[var(--color-secondary-text)]">
        {t('subscriptions.loading') || 'Loading subscription…'}
      </p>
    ) : (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <p className="text-sm text-[var(--color-secondary-text)]">
          {t('subscriptions.loading') || 'Loading subscription…'}
        </p>
      </div>
    );
  }

  const isIndividual = tenantType === 'INDIVIDUAL';
  const hasActiveSubscription = subscriptions.some((s) => s.status === 'active');

  return (
    <div className={embedded ? '' : 'max-w-6xl mx-auto px-4 py-8'}>
      {!embedded && (
        <>
          <h1 className="text-2xl font-semibold text-[var(--color-surface-text)] mb-2">
            {isIndividual
              ? t('subscriptions.heading.individual') || 'Your subscription'
              : t('subscriptions.heading.org') || 'Subscription & Billing'}
          </h1>
          <p className="text-sm text-[var(--color-secondary-text)] mb-6">
            {isIndividual
              ? t('subscriptions.subtitle.individual') ||
                'View your plan and buy top-ups for AI tokens.'
              : t('subscriptions.subtitle.org') ||
                'View your plan, approve pending requests, and buy top-ups for AI tokens.'}
          </p>
        </>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Current subscriptions */}
      <div className="bg-[var(--color-surface-bg)] shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
            {isIndividual
              ? t('subscriptions.current.heading.individual') || 'Your plan'
              : t('subscriptions.current.heading.org') || 'Current subscriptions'}
          </h2>
          {canManage ? (
            <button
              type="button"
              onClick={() => setShowSubscribeModal(true)}
              className="px-3 py-1.5 text-sm rounded-md bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-primary-bg)]/90 flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" />
              {hasActiveSubscription
                ? t('subscriptions.current.changePlan') || 'Change plan'
                : t('subscriptions.current.subscribe') || 'Subscribe'}
            </button>
          ) : null}
        </div>
        {subscriptions.length === 0 ? (
          <p className="text-sm text-[var(--color-secondary-text)]">
            {t('subscriptions.current.empty') || 'No subscriptions. Use Subscribe to add a plan.'}
          </p>
        ) : (
          <div className="border border-[var(--color-border)] rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-[var(--color-border)]">
              <thead className="bg-[var(--color-secondary-bg)]">
                <tr>
                  {hideTopUpSection && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                      {t('subscriptions.table.user') || 'User'}
                    </th>
                  )}
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.table.plan') || 'Plan'}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.table.tier') || 'Tier'}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.table.status') || 'Status'}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.table.period') || 'Period'}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.table.actions') || 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[var(--color-surface-bg)] divide-y divide-[var(--color-border)]">
                {subscriptions.map((s) => (
                  <tr key={s.id}>
                    {hideTopUpSection && (
                      <td className="px-4 py-2 text-sm text-[var(--color-surface-text)]">
                        {s.ownerUserName ?? '—'}
                      </td>
                    )}
                    <td className="px-4 py-2 text-sm text-[var(--color-surface-text)]">
                      {s.planName}
                      {s.billingPeriod
                        ? ` – ${s.billingPeriod.charAt(0).toUpperCase() + s.billingPeriod.slice(1)}`
                        : ''}
                    </td>
                    <td className="px-4 py-2 text-sm text-[var(--color-secondary-text)]">
                      <div className="flex flex-col">
                        <span>{s.tierName}</span>
                        <span className="text-xs text-[var(--color-secondary-text)]">
                          {s.dailyTokenCap > 0
                            ? `${s.dailyTokenCap.toLocaleString()} tokens / day`
                            : 'Tokens per day: N/A'}
                          {s.defaultModelDisplayName
                            ? ` • Model: ${s.defaultModelDisplayName}`
                            : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        s.status === 'active' ? 'bg-green-50 text-green-700' :
                        s.status === 'pending_approval' ? 'bg-amber-50 text-amber-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-[var(--color-secondary-text)]">
                      {new Date(s.periodStart).toLocaleDateString()} – {new Date(s.periodEnd).toLocaleDateString()}
                      {s.cancelAtPeriodEnd && <span className="ml-1 text-amber-600">(cancels at period end)</span>}
                    </td>
                    <td className="px-4 py-2 text-sm text-right space-x-3">
                      {(s.status === 'active' || s.status === 'trialing') && canManage && (
                        <>
                          {s.hasLinkedPaymentProvider && (
                            <button
                              type="button"
                              onClick={() => handleUpdateCardClick(s.id)}
                              disabled={!!actionLoading}
                              className="text-[var(--color-info)] hover:underline disabled:opacity-50"
                            >
                              {t('subscriptions.actions.updateCard') || 'Update card'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleCancel(s.id, true)}
                            disabled={!!actionLoading}
                            className="text-[var(--color-info)] hover:underline disabled:opacity-50"
                          >
                            {t('subscriptions.actions.cancelAtPeriodEnd') || 'Cancel at period end'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCancel(s.id, false)}
                            disabled={!!actionLoading}
                            className="text-red-600 hover:underline disabled:opacity-50"
                          >
                            {t('subscriptions.actions.cancelNow') || 'Cancel now'}
                          </button>
                        </>
                      )}
                      {s.status === 'active' && !canManage && <span className="text-[var(--color-secondary-text)]">—</span>}
                      {s.status === 'pending_approval' && (
                        <span className="text-[var(--color-secondary-text)]">
                          {t('subscriptions.status.awaitingApproval') || 'Awaiting approval'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending approvals (only for organization tenants) */}
      {!isIndividual && pending.length > 0 && (
        <div className="bg-[var(--color-surface-bg)] shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
            {t('subscriptions.pending.heading') || 'Pending approval'}
          </h2>
          <div className="border border-[var(--color-border)] rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-[var(--color-border)]">
              <thead className="bg-[var(--color-secondary-bg)]">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.table.plan') || 'Plan'}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.table.tier') || 'Tier'}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.pending.requested') || 'Requested'}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                    {t('subscriptions.table.actions') || 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[var(--color-surface-bg)] divide-y divide-[var(--color-border)]">
                {pending.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2 text-sm text-[var(--color-surface-text)]">{p.planName}</td>
                    <td className="px-4 py-2 text-sm text-[var(--color-secondary-text)]">{p.tierName}</td>
                    <td className="px-4 py-2 text-sm text-[var(--color-secondary-text)]">{new Date(p.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-right flex justify-end gap-2">
                      {canManage ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleApprove(p.id)}
                            disabled={actionLoading === p.id}
                            className="p-1.5 rounded text-green-600 hover:bg-green-50 disabled:opacity-50"
                            title={t('subscriptions.actions.approve') || 'Approve'}
                          >
                            <CheckIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(p.id)}
                            disabled={actionLoading === p.id}
                            className="p-1.5 rounded text-red-600 hover:bg-red-50 disabled:opacity-50"
                            title={t('subscriptions.actions.reject') || 'Reject'}
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </>
                      ) : (
                        <span className="text-[var(--color-secondary-text)]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top-ups (per user; hidden on tenant config page) */}
      {!hideTopUpSection && (
        <div className="bg-[var(--color-surface-bg)] shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
              {t('subscriptions.topups.heading') || 'Top-ups'}
            </h2>
            <div className="flex items-center gap-4">
              <span className="text-sm text-[var(--color-secondary-text)]">
                {t('subscriptions.topups.balancePrefix') || 'Top-up balance:'}{' '}
                <strong>${topUpBalanceUsd.toFixed(2)}</strong>
              </span>
              {canManage && (
                <button
                  type="button"
                  onClick={() => setShowTopUpModal(true)}
                  className="px-3 py-1.5 text-sm rounded-md bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-primary-bg)]/90 flex items-center gap-1"
                >
                  <BanknotesIcon className="h-4 w-4" />
                  {t('subscriptions.topups.buyTopup') || 'Buy top-up'}
                </button>
              )}
            </div>
          </div>
          {topups.length === 0 ? (
            <p className="text-sm text-[var(--color-secondary-text)]">
              {t('subscriptions.topups.empty') || 'No top-ups yet.'}
            </p>
          ) : (
            <div className="border border-[var(--color-border)] rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-[var(--color-border)]">
                <thead className="bg-[var(--color-secondary-bg)]">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                      {t('subscriptions.topups.table.amount') || 'Amount'}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">
                      {t('subscriptions.topups.table.date') || 'Date'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--color-surface-bg)] divide-y divide-[var(--color-border)]">
                  {topups.map((t) => (
                    <tr key={t.id}>
                      <td className="px-4 py-2 text-sm text-[var(--color-surface-text)]">${t.amountUsd.toFixed(2)} {t.currency.toUpperCase()}</td>
                      <td className="px-4 py-2 text-sm text-[var(--color-secondary-text)]">{new Date(t.purchasedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Usage & tokens (loaded on demand when this tab is visible) */}
      <div className="bg-[var(--color-surface-bg)] shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-2">
          {t('subscriptions.usage.heading') || 'Usage & tokens'}
        </h2>
        <p className="text-sm text-[var(--color-secondary-text)] mb-3">
          {t('subscriptions.usage.description') ||
            'Token usage by model and user, scoped to your account.'}
        </p>
        <ModelUsageReport embedded />
      </div>

      {/* Subscribe modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            {paymentClientSecret && paymentModal === 'subscription' && stripePublishableKey ? (
              <>
                <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">Complete payment</h3>
                <StripePaymentForm
                  clientSecret={paymentClientSecret}
                  publishableKey={stripePublishableKey}
                  onSuccess={() => handlePaymentSuccess('subscription')}
                  onCancel={handlePaymentCancel}
                  submitLabel="Subscribe"
                  billingDetails={{
                    name: [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || undefined,
                    email: user?.email || undefined,
                  }}
                />
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">Add subscription</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Plan</label>
                    <select
                      value={subscribePlanId}
                      onChange={(e) => {
                        const plan = plans.find((p) => p.id === e.target.value);
                        setSubscribePlanId(e.target.value);
                        if (plan) setSubscribeTierId(plan.aiTierId);
                        setSubscribePromoCode('');
                        setSubscribePricePreview(null);
                      }}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                    >
                      <option value="">Select plan</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} – {p.billingPeriod} ${p.priceUsd.toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Promo code (optional)</label>
                    <input
                      type="text"
                      value={subscribePromoCode}
                      onChange={(e) => setSubscribePromoCode(e.target.value)}
                      onBlur={async () => {
                        if (!subscribePlanId) {
                          return;
                        }
                        try {
                          const trimmedCode = subscribePromoCode.trim();
                          const payload: {
                            billingPlanId: string;
                            isTopUp: boolean;
                            promoCode?: string;
                          } = {
                            billingPlanId: subscribePlanId,
                            isTopUp: false,
                          };
                          if (trimmedCode) payload.promoCode = trimmedCode;
                          const res = await api.post('/ai/billing/price-preview', payload);
                          setSubscribePricePreview(res.data);
                        } catch (err: unknown) {
                          setSubscribePricePreview(null);
                          if (subscribePromoCode.trim()) {
                            const e = err as { response?: { data?: { message?: string } } };
                            toast.error(e.response?.data?.message || 'Promo code could not be applied.');
                          }
                        }
                      }}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                      placeholder="Enter promo code"
                    />
                    {subscribePricePreview && (
                      <p className="mt-2 text-xs text-[var(--color-secondary-text)]">
                        Base price: ${subscribePricePreview.basePriceUsd.toFixed(2)}
                        {subscribePricePreview.discountUsd > 0 && (
                          <>
                            {' '}· Discount: -${subscribePricePreview.discountUsd.toFixed(2)}
                            {' '}· Final: <span className="font-semibold text-[var(--color-surface-text)]">
                              ${subscribePricePreview.finalPriceUsd.toFixed(2)}
                            </span>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSubscribeModal(false);
                      setSubscribePlanId('');
                      setSubscribeTierId('');
                    }}
                    className="px-4 py-2 border border-[var(--color-border)] rounded-md text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleCreateSubscription()}
                    disabled={actionLoading === 'subscribe'}
                    className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:bg-[var(--color-primary-bg)]/90 disabled:opacity-50"
                  >
                    Subscribe
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Top-up modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            {paymentClientSecret && paymentModal === 'topup' && stripePublishableKey ? (
              <>
                <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">Complete payment</h3>
                <StripePaymentForm
                  clientSecret={paymentClientSecret}
                  publishableKey={stripePublishableKey}
                  onSuccess={() => handlePaymentSuccess('topup')}
                  onCancel={handlePaymentCancel}
                  submitLabel="Pay"
                  billingDetails={{
                    name: [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || undefined,
                    email: user?.email || undefined,
                  }}
                />
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">Buy top-up</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Subscription</label>
                    <select
                      value={topUpSubscriptionId}
                      onChange={(e) => setTopUpSubscriptionId(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                    >
                      <option value="">Select subscription</option>
                      {subscriptions.filter((s) => s.status === 'active').map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.planName}
                          {s.billingPeriod
                            ? ` – ${s.billingPeriod.charAt(0).toUpperCase() + s.billingPeriod.slice(1)}`
                            : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Amount (USD)</label>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      value={topUpAmount}
                      onChange={(e) => {
                        setTopUpAmount(e.target.value);
                        setTopUpPricePreview(null);
                      }}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                      placeholder="e.g. 25"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Promo code (optional)</label>
                    <input
                      type="text"
                      value={topUpPromoCode}
                      onChange={(e) => setTopUpPromoCode(e.target.value)}
                      onBlur={async () => {
                        const amount = parseFloat(topUpAmount);
                        if (!topUpSubscriptionId || !Number.isFinite(amount) || amount <= 0) {
                          return;
                        }
                        const activeSub = subscriptions.find((s) => s.id === topUpSubscriptionId) || subscriptions.find((s) => s.status === 'active');
                        if (!activeSub) {
                          return;
                        }
                        try {
                          const trimmedCode = topUpPromoCode.trim();
                          const payload: {
                            billingPlanId: string;
                            isTopUp: boolean;
                            amountUsd: number;
                            promoCode?: string;
                          } = {
                            billingPlanId: activeSub.id ? activeSub.id : '',
                            isTopUp: true,
                            amountUsd: amount,
                          };
                          if (trimmedCode) payload.promoCode = trimmedCode;
                          const res = await api.post('/ai/billing/price-preview', payload);
                          setTopUpPricePreview(res.data);
                        } catch (err: unknown) {
                          setTopUpPricePreview(null);
                          if (topUpPromoCode.trim()) {
                            const e = err as { response?: { data?: { message?: string } } };
                            toast.error(e.response?.data?.message || 'Promo code could not be applied.');
                          }
                        }
                      }}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                      placeholder="Enter promo code"
                    />
                    {topUpPricePreview && (
                      <p className="mt-2 text-xs text-[var(--color-secondary-text)]">
                        Amount: ${topUpPricePreview.basePriceUsd.toFixed(2)}
                        {topUpPricePreview.discountUsd > 0 && (
                          <>
                            {' '}· Discount: -${topUpPricePreview.discountUsd.toFixed(2)}
                            {' '}· Final: <span className="font-semibold text-[var(--color-surface-text)]">
                              ${topUpPricePreview.finalPriceUsd.toFixed(2)}
                            </span>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTopUpModal(false);
                      setTopUpAmount('');
                      setTopUpSubscriptionId('');
                    }}
                    className="px-4 py-2 border border-[var(--color-border)] rounded-md text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleBuyTopUp()}
                    disabled={actionLoading === 'topup'}
                    className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:bg-[var(--color-primary-bg)]/90 disabled:opacity-50"
                  >
                    Buy top-up
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Update card modal (same pattern as tenant-subscription-configuration) */}
      {paymentModal === 'update-card' && paymentClientSecret && stripePublishableKey && updateCardSubscriptionId && updateCardSetupIntentId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
              {t('subscriptions.actions.updateCard') || 'Update payment method'}
            </h3>
            <StripeSetupForm
              clientSecret={paymentClientSecret}
              publishableKey={stripePublishableKey}
              submitLabel={t('subscriptions.actions.saveCard') || 'Save card'}
              billingDetails={{
                name: [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || undefined,
                email: user?.email || undefined,
              }}
              onSuccess={async () => {
                try {
                  await api.post(`/ai/billing/subscriptions/${updateCardSubscriptionId}/payment-method/confirm`, {
                    setupIntentId: updateCardSetupIntentId,
                  });
                  toast.success(t('subscriptions.paymentMethodUpdated') || 'Payment method updated.');
                  setPaymentClientSecret(null);
                  setPaymentModal(null);
                  setUpdateCardSubscriptionId(null);
                  setUpdateCardSetupIntentId(null);
                  void load();
                } catch (e: unknown) {
                  const err = e as { response?: { data?: { error?: string; message?: string } } };
                  toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to save payment method');
                }
              }}
              onCancel={() => {
                setPaymentClientSecret(null);
                setPaymentModal(null);
                setUpdateCardSubscriptionId(null);
                setUpdateCardSetupIntentId(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
